
import React from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog.tsx';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { serviceFactory } from '@/services/core/serviceFactory.ts';
import { CareSpecialistService } from '@/services/features/CareSpecialistService.ts';

interface CareSpecialist {
  care_specialist_id: number;
  full_name: string;
}

interface DeleteCareSpecialistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  careSpecialist: CareSpecialist | null;
  onSuccess: () => void;
}

const DeleteCareSpecialistDialog = ({ open, onOpenChange, careSpecialist, onSuccess }: DeleteCareSpecialistDialogProps) => {
  const { user, token, hasuraToken } = useAuth();
  const { toast } = useToast();

  const handleDelete = async () => {
    if (!careSpecialist || !user?.id || !token) return;

    try {
      // Update service context with current auth info - FIXED: Use correct token types
      serviceFactory.updateGlobalContext({
        hasuraToken: hasuraToken,
        userId: user.id,
        token: token,
        userType: user.type
      });

      const careSpecialistService = serviceFactory.getService(CareSpecialistService);
      const result = await careSpecialistService.deleteCareSpecialist(careSpecialist.care_specialist_id);
      
      if (result.success !== false) {
        toast({
          title: "Success",
          description: "Care Specialist deleted successfully",
        });
        onSuccess();
        onOpenChange(false);
      } else {
        throw new Error(result.message || 'Failed to delete care specialist');
      }
    } catch (err) {
      console.error('Error deleting care specialist:', err);
      toast({
        title: "Delete Failed",
        description: err instanceof Error ? err.message : "Failed to delete care specialist",
        variant: "destructive"
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-white rounded-2xl shadow-2xl border-0">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-semibold text-gray-800">
            Are you sure you want to delete the Care Specialist?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-600">
            This action will permanently delete {careSpecialist?.full_name}. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-lg border-gray-300 hover:bg-gray-50">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteCareSpecialistDialog;
